import { useEffect, useMemo } from 'react';
import { RouteObject, useLocation, useNavigate } from 'react-router';
import { Navigate, Outlet } from 'react-router';

import { authRoutes, githubCallbackRoutes } from '@fly/features/auth';
import { dashboardRoutes } from '@fly/features/dashboard';
import { useDownloadRoutes } from '@fly/features/download';
import { signUpRoutes } from '@fly/features/sign-up';
import { FeatureToggle, routes, setNavigateFunction } from '@fly/shared';
import { useFeatureToggle } from '@fly/shared-hooks';
import { FlyErrorPage, FlyNotFound } from '@fly/shared-ui';

// Root layout component that wraps all routes
const RootLayout = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  return <Outlet />;
};

/** Strips the legacy /dashboard prefix from old deep links (emails, Slack, bookmarks).*/
const LegacyDashboardRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const stripped = pathname.replace(/^\/dashboard(?=\/|$)/, '') || '';
  const target =
    stripped === '' || stripped === '/' ? routes.DASHBOARD.ROOT : stripped;
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

export function useAppRoutes(): RouteObject[] {
  const downloadRoutes = useDownloadRoutes();
  const { hasFeature } = useFeatureToggle();
  const signupSunset = hasFeature(FeatureToggle.SIGNUP_SUNSET);

  return useMemo(
    () => [
      {
        element: <RootLayout />,
        errorElement: <FlyErrorPage />,
        children: [
          { path: '/dashboard/*', element: <LegacyDashboardRedirect /> },
          ...authRoutes,
          ...(!signupSunset ? signUpRoutes : []),
          ...dashboardRoutes,
          ...(!signupSunset ? downloadRoutes : []),
          ...githubCallbackRoutes,
          {
            path: '*',
            element: <FlyNotFound />,
          },
        ],
      },
    ],
    [downloadRoutes, signupSunset]
  );
}
